import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  FieldError,
  Input,
  Label
} from '@repo/ui'
import { useForm } from '@tanstack/react-form'
import { Link, createFileRoute, redirect, useRouter } from '@tanstack/react-router'
import * as Sentry from '@sentry/react'
import { useEffect, useRef, useState } from 'react'
import { z } from 'zod'
import { apiSetPassword } from '@/api/user/setPassword'
import { apiPasswordReset } from '@/api/user/passwordReset'
import { zodFieldValidator } from '@/common/parseDataType'
import { apiLogin } from '@/api/user/login'
import { apiMfa, apiMfaResend } from '@/api/user/mfa'
import { apiUserOrgProfile } from '@/api/user/profile'
import { isApiResponseError } from '@/api/errorHandler'

const UserPasswordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/[0-9]/, 'Password must contain at least one number')
  .regex(/[^A-Za-z0-9]/, 'Password must contain at least one special character')

const emailFieldSchema = z.email('Please enter a valid email address')

export const Route = createFileRoute('/user/set-password')({
  validateSearch: z.object({
    email: z.string().optional().catch(''),
    token: z.string().optional().catch('')
  }),
  beforeLoad: ({ search }) => {
    if (!search.email || !search.token) {
      throw redirect({ to: '/login' })
    }
  },
  head: () => ({ meta: [{ title: 'Set Password - Snowvault' }] }),
  component: SetPassword
})

type PageState = 'set-password' | 'mfa' | 'resend' | 'resend-success' | 'success'

function SetPassword() {
  const { email, token } = Route.useSearch()
  const router = useRouter()
  const [pageState, setPageState] = useState<PageState>('set-password')
  const [setPasswordError, setSetPasswordError] = useState<string | undefined>(undefined)
  const [mfaError, setMfaError] = useState<string | undefined>(undefined)
  const [resendError, setResendError] = useState<string | undefined>(undefined)
  const [resendCooldown, setResendCooldown] = useState(0)
  const redirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current)
    }
  }, [])

  useEffect(() => {
    if (resendCooldown <= 0) return
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000)
    return () => clearTimeout(timer)
  }, [resendCooldown])

  async function handleMfaResend() {
    if (!email) return
    try {
      await apiMfaResend(email)
      setResendCooldown(60)
    } catch (err) {
      if (!isApiResponseError(err)) Sentry.captureException(err)
      console.error('MFA resend error:', err)
      setMfaError('Failed to resend code. Please try again.')
    }
  }

  const setPasswordForm = useForm({
    defaultValues: {
      password1: '',
      password2: ''
    },
    onSubmit: async ({ value }) => {
      setSetPasswordError(undefined)
      try {
        await apiSetPassword({
          email: email ?? '',
          verification_token: token ?? '',
          password1: value.password1,
          password2: value.password2
        })

        try {
          const loginRes = await apiLogin({ email: email ?? '', password: value.password1 })
          if (loginRes.mfa_required) {
            setPageState('mfa')
            return
          }
          const userOrgProfile = await apiUserOrgProfile()
          router.update({
            context: {
              ...router.options.context,
              user: { isAuthenticated: true, userOrgProfile }
            }
          })
          setPageState('success')
          redirectTimerRef.current = setTimeout(() => {
            void router.navigate({ to: '/$orgSlug', params: { orgSlug: userOrgProfile.org.slug } })
          }, 5000)
        } catch {
          void router.navigate({ to: '/login' })
        }
      } catch (err) {
        if (!isApiResponseError(err)) Sentry.captureException(err)
        console.error('Set password error:', err)
        setSetPasswordError(err instanceof Error ? err.message : 'Failed to set password. Please try again.')
      }
    }
  })

  const mfaForm = useForm({
    defaultValues: {
      mfaCode: '',
      rememberDevice: false
    },
    onSubmit: async ({ value }) => {
      setMfaError(undefined)
      try {
        const userOrgProfile = await apiMfa({
          email: email ?? '',
          code: value.mfaCode,
          remember_device: value.rememberDevice
        })
        router.update({
          context: {
            ...router.options.context,
            user: { isAuthenticated: true, userOrgProfile }
          }
        })
        setPageState('success')
        redirectTimerRef.current = setTimeout(() => {
          void router.navigate({ to: '/$orgSlug', params: { orgSlug: userOrgProfile.org.slug } })
        }, 5000)
      } catch (err) {
        if (!isApiResponseError(err)) Sentry.captureException(err)
        console.error('MFA error:', err)
        setMfaError('Verification failed. Please check your code and try again.')
      }
    }
  })

  const resendForm = useForm({
    defaultValues: {
      email: email ?? ''
    },
    onSubmit: async ({ value }) => {
      setResendError(undefined)
      try {
        await apiPasswordReset(value.email)
        setPageState('resend-success')
      } catch (err) {
        if (!isApiResponseError(err)) Sentry.captureException(err)
        console.error('Resend error:', err)
        setResendError(err instanceof Error ? err.message : 'Failed to send a new link. Please try again.')
      }
    }
  })

  if (pageState === 'success') {
    return (
      <div className="flex min-h-svh w-full items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <Card className="gap-4">
            <CardHeader>
              <CardTitle>Password set</CardTitle>
              <CardDescription>Your password has been set successfully.</CardDescription>
              <CardDescription>Logging in automatically...</CardDescription>
            </CardHeader>
            <CardContent>
              <Link to="/login" className="text-sm underline underline-offset-4 hover:no-underline">
                Sign in now
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  if (pageState === 'mfa') {
    return (
      <div className="flex min-h-svh w-full items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <Card className="gap-4">
            <CardHeader>
              <CardTitle>Check your email</CardTitle>
              <CardDescription>Enter the verification code sent to your email.</CardDescription>
            </CardHeader>
            <CardContent>
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  mfaForm.handleSubmit()
                }}
                className="space-y-4"
              >
                <mfaForm.Field name="mfaCode">
                  {(field) => (
                    <div className="space-y-1.5">
                      <Label htmlFor={field.name}>Verification code</Label>
                      <Input
                        id={field.name}
                        name={field.name}
                        type="text"
                        placeholder="Enter 6-digit code"
                        maxLength={6}
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(e) => field.handleChange(e.target.value)}
                      />
                      <FieldError errors={field.state.meta.errors.map((e) => ({ message: String(e) }))} />
                    </div>
                  )}
                </mfaForm.Field>

                <mfaForm.Field name="rememberDevice">
                  {(field) => (
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id={field.name}
                        checked={field.state.value}
                        onCheckedChange={(checked) => field.handleChange(checked === true)}
                      />
                      <Label htmlFor={field.name} className="font-normal">
                        Remember this device for 30 days
                      </Label>
                    </div>
                  )}
                </mfaForm.Field>

                {mfaError && <p className="text-sm text-destructive">{mfaError}</p>}

                <div className="flex items-center justify-between">
                  <mfaForm.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
                    {([canSubmit, isSubmitting]) => (
                      <Button type="submit" size="sm" disabled={!canSubmit || isSubmitting}>
                        {isSubmitting ? 'Verifying...' : 'Verify'}
                      </Button>
                    )}
                  </mfaForm.Subscribe>
                  <button
                    type="button"
                    disabled={resendCooldown > 0}
                    onClick={handleMfaResend}
                    className="cursor-pointer text-sm underline underline-offset-4 hover:no-underline disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : 'Resend code'}
                  </button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  if (pageState === 'resend-success') {
    return (
      <div className="flex min-h-svh w-full items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <Card className="gap-4">
            <CardHeader>
              <CardTitle>Invitation sent</CardTitle>
              <CardDescription>A new invitation is on its way — check your inbox.</CardDescription>
            </CardHeader>
            <CardContent>
              <Link to="/login" className="text-sm underline underline-offset-4 hover:no-underline">
                Back to sign in
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  if (pageState === 'resend') {
    return (
      <div className="flex min-h-svh w-full items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <Card className="gap-4">
            <CardHeader>
              <CardTitle>Request a new link</CardTitle>
              <CardDescription>Enter your email and we will send you a new invitation.</CardDescription>
            </CardHeader>
            <CardContent>
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  resendForm.handleSubmit()
                }}
                className="space-y-4"
              >
                <resendForm.Field name="email" validators={{ onChange: zodFieldValidator(emailFieldSchema) }}>
                  {(field) => (
                    <div className="space-y-1.5">
                      <Label htmlFor={field.name}>Email</Label>
                      <Input
                        id={field.name}
                        name={field.name}
                        type="email"
                        placeholder="Enter your email"
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(e) => field.handleChange(e.target.value)}
                      />
                      <FieldError errors={field.state.meta.errors.map((e) => ({ message: String(e) }))} />
                    </div>
                  )}
                </resendForm.Field>

                {resendError && <p className="text-sm text-destructive">{resendError}</p>}

                <div className="flex items-center justify-between">
                  <resendForm.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
                    {([canSubmit, isSubmitting]) => (
                      <Button type="submit" size="sm" disabled={!canSubmit || isSubmitting}>
                        {isSubmitting ? 'Sending...' : 'Send new invitation'}
                      </Button>
                    )}
                  </resendForm.Subscribe>
                  <Link to="/login" className="text-sm underline underline-offset-4 hover:no-underline">
                    Back to sign in
                  </Link>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-svh w-full items-center justify-center p-6">
      <div className="w-full max-w-sm">
        <Card className="gap-4">
          <CardHeader>
            <CardTitle>Set your password</CardTitle>
            <CardDescription>Choose a password to activate your account.</CardDescription>
          </CardHeader>
          <CardContent>
            <form
              onSubmit={(e) => {
                e.preventDefault()
                setPasswordForm.handleSubmit()
              }}
              className="space-y-4"
            >
              <setPasswordForm.Field name="password1" validators={{ onChange: zodFieldValidator(UserPasswordSchema) }}>
                {(field) => (
                  <div className="space-y-1.5">
                    <Label htmlFor={field.name}>Password</Label>
                    <Input
                      id={field.name}
                      name={field.name}
                      type="password"
                      placeholder="Enter your password"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                    />
                    <FieldError errors={field.state.meta.errors.map((e) => ({ message: String(e) }))} />
                  </div>
                )}
              </setPasswordForm.Field>

              <setPasswordForm.Field
                name="password2"
                validators={{
                  onChangeListenTo: ['password1'],
                  onChange: ({ value, fieldApi }) =>
                    value !== fieldApi.form.getFieldValue('password1') ? 'Passwords do not match' : undefined
                }}
              >
                {(field) => (
                  <div className="space-y-1.5">
                    <Label htmlFor={field.name}>Confirm password</Label>
                    <Input
                      id={field.name}
                      name={field.name}
                      type="password"
                      placeholder="Confirm your password"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                    />
                    <FieldError errors={field.state.meta.errors.map((e) => ({ message: String(e) }))} />
                  </div>
                )}
              </setPasswordForm.Field>

              {setPasswordError && (
                <div className="space-y-1">
                  <p className="text-sm text-destructive">{setPasswordError}</p>
                  <button
                    type="button"
                    onClick={() => setPageState('resend')}
                    className="cursor-pointer text-sm underline underline-offset-4 hover:no-underline"
                  >
                    Request a new link
                  </button>
                </div>
              )}

              <setPasswordForm.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
                {([canSubmit, isSubmitting]) => (
                  <Button type="submit" size="sm" disabled={!canSubmit || isSubmitting}>
                    {isSubmitting ? 'Setting password...' : 'Set password'}
                  </Button>
                )}
              </setPasswordForm.Subscribe>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
